/**
 * Standard header for every admin page. Resolves icon, title, description and
 * breadcrumbs from the central `adminPages` registry so they stay aligned with
 * the side nav. Compact by design - this is an admin tool, not a hero banner.
 *
 * Export actions never live here (RULES): they belong in the filters card
 * (beside Clear Filters) or the table toolbar. `actions` is for 1-2 PRIMARY
 * page actions only (e.g. Add / Connect).
 */
import React from 'react'
import { Helmet } from 'react-helmet-async'
import { Link as RouterLink } from 'react-router-dom'
import { Box, Breadcrumbs, Chip, Link, Stack, Typography } from '@mui/material'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'

import { poolwerxColors } from '../../../theme/colors'
import {
  getAdminPage,
  registryBreadcrumbs,
  type AdminPageKey,
  type BreadcrumbItem,
} from '../../../config/adminPages'

type ChipColor = 'default' | 'primary' | 'success' | 'warning' | 'error'

export interface AdminPageHeaderProps {
  pageKey: AdminPageKey
  /** Reactive title override - drives the heading, breadcrumb tail and doc title. */
  titleOverride?: string
  descriptionOverride?: string
  /** e.g. "107 partners" - shown as a chip beside the title. */
  countLabel?: string
  /** The entity this page acts on (FP name, user email) - shown as the same
   *  teal chip beside the title on detail/edit pages, so the heading stays a
   *  generic "Edit User" / "Manage Connections" while the identity reads clearly. */
  entityChip?: string
  statusChip?: React.ReactNode
  /** Inline slot directly after the title (e.g. a FavouriteStar + suffix). */
  titleAdornment?: React.ReactNode
  /** Override the registry-derived trail (dynamic detail/edit pages). */
  breadcrumbs?: BreadcrumbItem[]
  contextChips?: Array<{ label: string; icon?: React.ReactElement; color?: ChipColor }>
  /** 1-2 primary page actions, top-right. Never Export. */
  actions?: React.ReactNode
  /** Title portion of the document `<title>` when it must differ from the
   *  visible heading (e.g. heading "Edit Franchise Partner" but tab "Edit
   *  Poolwerx Albany Creek"). The standard suffix is always appended.
   *  Defaults to titleOverride ?? registry label. */
  helmetTitle?: string
  /** Merged into the root Box (default spacing is `mb: 3`, or `mb: 1.5` when
   *  `dense`). Lets constrained detail/form pages tighten the gap to the card
   *  below without diverging from the standard header. */
  sx?: object
  /** Compact form variant: smaller h5 title + icon tile and a tighter default
   *  bottom margin, so add/edit pages read as admin forms, not landing pages. */
  dense?: boolean
  /** Replace the registry icon in the header tile (e.g. a brand logo image that
   *  isn't an MUI `SvgIconComponent`). Defaults to the registry icon. */
  iconOverride?: React.ReactNode
}

const HELMET_SUFFIX = ' - Inspector Tool Admin'

const AdminPageHeader: React.FC<AdminPageHeaderProps> = ({
  pageKey,
  titleOverride,
  descriptionOverride,
  countLabel,
  entityChip,
  statusChip,
  titleAdornment,
  breadcrumbs,
  contextChips,
  actions,
  helmetTitle,
  sx,
  dense = false,
  iconOverride,
}) => {
  const page = getAdminPage(pageKey)
  const title = titleOverride ?? page.label
  const description = descriptionOverride ?? page.description
  const crumbs = breadcrumbs ?? registryBreadcrumbs(pageKey, titleOverride)
  const Icon = page.icon
  const tileSize = dense ? 32 : 40
  // Add/edit forms (dense) keep just breadcrumbs + icon/title - no sub-text line.
  const showDescription = Boolean(description) && !dense
  // Shared teal chip styling for both the count label and the entity chip.
  const tealChipSx = {
    backgroundColor: poolwerxColors.tealLight,
    border: `1px solid ${poolwerxColors.aqua}`,
    color: poolwerxColors.charcoal,
    fontWeight: 500,
    fontSize: '0.75rem',
    height: 24,
  }

  return (
    <Box sx={{ mt: dense ? '5px' : 0, mb: dense ? 2.5 : 3, ...sx }}>
      <Helmet>
        <title>{(helmetTitle ?? title) + HELMET_SUFFIX}</title>
      </Helmet>

      {/* Breadcrumb row - always present */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 1, '& .MuiBreadcrumbs-li': { fontSize: '0.8125rem' } }}
      >
        {crumbs.map((crumb, idx) => {
          const isLast = idx === crumbs.length - 1
          if (crumb.to && !isLast) {
            return (
              <Link
                key={`${crumb.label}-${idx}`}
                component={RouterLink}
                to={crumb.to}
                underline="hover"
                color="inherit"
                sx={{ fontSize: '0.8125rem' }}
              >
                {crumb.label}
              </Link>
            )
          }
          return (
            <Typography key={`${crumb.label}-${idx}`} color="text.primary" sx={{ fontSize: '0.8125rem' }}>
              {crumb.label}
            </Typography>
          )
        })}
      </Breadcrumbs>

      {/* Header row: icon + title (+ count/status + adornment) | primary actions */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: showDescription ? 0.5 : 0 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: tileSize,
              height: tileSize,
              borderRadius: 1.5,
              backgroundColor: poolwerxColors.tealLight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {iconOverride ?? (
              <Icon sx={{ color: poolwerxColors.teal, fontSize: dense ? 20 : 24 }} />
            )}
          </Box>
          <Typography variant={dense ? 'h5' : 'h4'} component="h1" sx={{ minWidth: 0 }}>
            {title}
          </Typography>
          {titleAdornment}
          {countLabel && <Chip label={countLabel} size="small" sx={tealChipSx} />}
          {entityChip && <Chip label={entityChip} size="small" sx={tealChipSx} />}
          {statusChip}
        </Stack>
        {actions && <Box sx={{ flexShrink: 0 }}>{actions}</Box>}
      </Stack>

      {/* Sub-text row (suppressed on dense add/edit form headers) */}
      {showDescription && (
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      )}

      {/* Context chip row */}
      {contextChips && contextChips.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
          {contextChips.map((chip, idx) => (
            <Chip
              key={`${chip.label}-${idx}`}
              label={chip.label}
              icon={chip.icon}
              size="small"
              variant="outlined"
              color={chip.color ?? 'default'}
            />
          ))}
        </Stack>
      )}
    </Box>
  )
}

export default AdminPageHeader
